<template>
  <div class="bp-input" :class="componentClasses">
    <label :for="id" class="bp-input__label" :class="labelClasses" v-if="label">
      {{ label }}
      <span v-if="required" class="text-negative-700">*</span>
      <bp-tooltip v-if="info" :text="info">
        <bp-icon
          :size="labelIconSize"
          icon="fa-regular fa-exclamation-circle"
          class="text-information-400"
        />
      </bp-tooltip>
    </label>

    <div class="bp-input__container" @click="onClick">
      <bp-icon
        v-if="preIcon"
        class="text-neutral-600"
        :size="iconSize"
        :icon="preIcon"
      />

      <input
        v-if="password"
        v-model="innerValue"
        ref="input"
        class="bp-input__inner"
        :disabled="disabled"
        :class="inputClasses"
        :placeholder="placeholder"
        :type="show ? 'text' : 'password'"
        @blur="onBlur"
      />

      <the-mask
        v-else-if="mask"
        v-model="innerValue"
        ref="input"
        class="bp-input__inner"
        :mask="mask"
        :masked="masked"
        :disabled="disabled"
        :class="inputClasses"
        :placeholder="placeholder"
      />

      <textarea
        v-else-if="textarea"
        v-model="innerValue"
        ref="input"
        type="text"
        class="bp-input__inner"
        :rows="rows"
        :disabled="disabled"
        :class="inputClasses"
        :placeholder="placeholder"
      />

      <input
        v-else-if="number"
        v-model="innerValue"
        ref="input"
        type="number"
        class="bp-input__inner"
        :disabled="disabled"
        :class="inputClasses"
        :placeholder="placeholder"
        @keydown="checkInput"
        @blur="onBlur"
      />

      <money-input
        v-else-if="money"
        v-model="innerValue"
        v-bind="moneyConfig"
        ref="input"
        class="bp-input__inner"
        :disabled="disabled"
        :class="inputClasses"
      />

      <input
        v-else
        v-model="innerValue"
        ref="input"
        type="text"
        class="bp-input__inner"
        :disabled="disabled"
        :class="inputClasses"
        :placeholder="placeholder"
        @blur="onBlur"
        @keydown="checkInput"
      />

      <slot name="trailing-button">
        <bp-button
          v-if="password"
          size="sm"
          color="neutral"
          variant="glyph"
          :icon="show ? 'fa-light fa-eye-slash' : 'fa-light fa-eye'"
          @click="show = !show"
        />

        <bp-icon
          v-else-if="trailingIcon"
          :size="iconSize"
          :icon="trailingIcon"
          :class="errorMessage ? 'text-negative-600' : 'text-neutral-600'"
        />
      </slot>
    </div>

    <div
      v-if="description || limit"
      class="bp-input__description-container"
      :class="descriptionContainerClasses"
    >
      <div
        v-if="description"
        class="bp-input__description"
        :class="descriptionClasses"
      >
        {{ description }}
      </div>

      <div
        class="bp-input__limit"
        :class="{ 'text-negative-600': exceedsLimit }"
        v-if="limit"
      >
        {{ innerValue ? innerValue.length : 0 }}/{{ limit }}
      </div>
    </div>
    <div class="bp-input__error" :class="descriptionClasses">
      {{ errorMessage }}
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import { Money } from "v-money";
import { TheMask } from "vue-the-mask";

let timeout;
export default {
  components: {
    "money-input": Money,
    TheMask
  },
  props: {
    value: {
      type: [String, Number]
    },
    id: {
      type: String,
      default: "bp-input"
    },
    size: {
      type: String,
      default: "lg",
      validation: val => ["xl", "lg", "md", "sm"].includes(val)
    },
    label: {
      type: String
    },
    preIcon: {
      type: String
    },
    placeholder: {
      type: String
    },
    description: {
      type: String
    },
    limit: {
      type: Number
    },
    required: {
      type: Boolean
    },
    errors: {
      type: [Array, String]
    },
    textarea: {
      type: Boolean,
      default: false
    },
    rows: {
      type: [Number, String],
      default: 5
    },
    number: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: -Infinity
    },
    max: {
      type: Number,
      default: Infinity
    },
    disabled: {
      type: Boolean,
      default: false
    },
    money: {
      type: Boolean,
      default: false
    },
    moneyConfig: {
      type: Object,
      default: () => ({
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        precision: 2,
        masked: false
      })
    },
    delay: {
      type: Number,
      default: 0
    },
    wholeNumber: {
      type: Boolean,
      default: false
    },
    outerButtons: {
      type: Boolean,
      default: false
    },
    mask: {
      type: String,
      default: null
    },
    masked: {
      type: Boolean,
      default: false
    },
    password: {
      type: Boolean,
      default: false
    },
    info: {
      type: String,
      default: null
    },
    validation: {
      type: Array,
      default: () => []
    },
    lock: {
      type: Boolean,
      default: false
    },
    link: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      innerValue: this.value,
      accessed: false,
      innerErrors: this.errors,
      show: false
    };
  },
  computed: {
    trailingIcon() {
      return this.errorMessage ? "fa fa-exclamation-circle" : null;
    },
    computedSize() {
      return ["xl", "lg", "md", "sm"].includes(this.size) ? this.size : "md";
    },
    exceedsLimit() {
      return (
        this.limit && this.innerValue && this.innerValue.length > this.limit
      );
    },
    minMaxError() {
      if ((this.number || this.money) && (this.required || this.innerValue)) {
        if (this.innerValue < this.min)
          return `O valor não deve ser menor que ${
            this.money ? this.moneyConfig?.currency || "R$" : ""
          } ${this.min}${this.money ? ",00" : "."}`;
        if (this.innerValue > this.max)
          return `O valor não deve ser maior que ${this.max}.`;
      }
      return undefined;
    },

    errorMessage() {
      if (this.innerErrors && this.innerErrors.length) {
        if (_.isArray(this.innerErrors)) return this.innerErrors[0];
        return this.innerErrors;
      }

      if (this.validation.length && this.accessed) {
        const validation = this.validation.map(func => func(this.innerValue));
        const error = validation.find(v => v !== true);
        if (error) return error;
      }

      if (this.accessed && this.required && !this.innerValue)
        return "Esse campo é obrigatório.";

      if (this.exceedsLimit)
        return `O texto não deve conter mais que ${this.limit} caracteres.`;

      if (this.accessed && this.minMaxError) return this.minMaxError;

      if (
        this.accessed &&
        (this.number || this.money) &&
        this.wholeNumber &&
        !!(this.innerValue % 1)
      )
        return "Digite um número inteiro.";

      return undefined;
    },

    // Computed classes
    componentClasses() {
      const classes = [];

      classes.push(this.computedSize);

      if (this.disabled) classes.push("disabled");

      if (this.errorMessage) classes.push("error");

      return classes;
    },
    labelClasses() {
      const classes = [];
      classes.push(
        ["xl", "lg"].includes(this.computedSize)
          ? "functional-1"
          : "functional-2"
      );
      return classes;
    },
    labelIconSize() {
      const sizes = {
        xl: 18,
        lg: 18,
        md: 12,
        sm: 12
      };
      return sizes[this.computedSize];
    },
    inputClasses() {
      const classes = [];

      const spacing = {
        xl: "px-4",
        lg: "px-3",
        md: "px-3",
        sm: "px-2"
      };
      classes.push(spacing[this.computedSize]);

      switch (this.computedSize) {
        case "md":
          classes.push("functional-1");
          break;
        case "sm":
          classes.push("functional-2");
          break;
      }

      if (!this.outerButtons) classes.push("hide-outer-buttons");
      if (this.link) classes.push("link");

      return classes;
    },
    iconSize() {
      const sizes = {
        xl: 20,
        lg: 18,
        md: 16,
        sm: 14
      };
      return sizes[this.computedSize];
    },
    descriptionContainerClasses() {
      const classes = [];

      classes.push(
        ["xl", "lg"].includes(this.computedSize)
          ? "functional-1"
          : "functional-2"
      );

      return classes;
    },
    descriptionClasses() {
      return [];
    }
  },
  methods: {
    checkInput(event) {
      if (this.number) {
        const invalidKeyCodes = [69, 190, 188, 229];
        if (invalidKeyCodes.includes(event.keyCode)) event.preventDefault();
      }
      if (
        this.lock &&
        this.innerValue.length == this.limit &&
        event.keyCode != 8
      ) {
        event.preventDefault();
      }
    },
    onClick() {
      if (this.money || this.mask) this.$refs.input.$el.focus();
      else this.$refs.input.focus();
    },
    onBlur() {
      this.accessed = true;
      if (typeof this.innerValue === "string")
        this.innerValue = this.innerValue.trim();
      this.$emit("blur", this.innerValue);
    },
    reset() {
      this.accessed = false;
    }
  },
  watch: {
    value(newVal) {
      this.innerValue = newVal;
      if (!newVal) this.accessed = false;
    },
    innerValue(newVal) {
      if (newVal != this.value) {
        this.$emit("input", newVal);

        this.innerErrors = [];

        clearTimeout(timeout);
        timeout = setTimeout(() => {
          this.$emit("change", newVal);
        }, this.delay);
      }
    },
    errors(newVal) {
      this.innerErrors = newVal;
    }
  },
  mounted() {
    if (this.money || this.mask)
      this.$refs.input.$el.addEventListener("blur", this.onBlur);
  }
};
</script>
<style lang="scss" scoped>
.bp-input {
  display: block;
  width: 100%;
  .bp-input__label {
    gap: 8px;
    margin: 0 0 4px;
    display: flex;
    color: #666666;
  }
  .bp-input__container {
    border-radius: 4px;
    border: 1px solid #a9a9a9;
    transition: 0.3s all ease-in-out;
    display: flex;
    align-items: center;
    overflow: hidden;

    cursor: text;
    transition: 0.2s border-color;
    .bp-input__inner {
      border: none;
      outline: none;

      flex: 1;
      background: transparent;

      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0px;

      &:not(textarea) {
        height: 24px;
      }

      &::placeholder {
        color: #a9a9a9;
      }

      &.hide-outer-buttons {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }
  }
  .bp-input__description-container {
    gap: 8px;
    display: flex;
    align-items: flex-start;
    .bp-input__description {
      flex: 1;
      color: #666666;
    }
    .bp-input__limit {
      color: #929292;
    }
  }
  // Sizes
  &.xl {
    .bp-input__container {
      padding: 16px;
    }
  }
  &.lg {
    .bp-input__container {
      padding: 12px;
    }
  }
  &.md {
    .bp-input__container {
      padding: 8px;
    }
  }
  &.sm {
    .bp-input__container {
      padding: 6px;

      .bp-input__inner:not(textarea) {
        height: 20px;
      }
    }
  }

  // Desktop sizes
  @media screen and (min-width: 992px) {
    &.xl {
      .bp-input__container {
        padding: 11px;
      }
    }
    &.lg {
      .bp-input__container {
        padding: 8px;
      }
    }
    &.md {
      .bp-input__container {
        padding: 4px;
      }
    }
    &.sm {
      .bp-input__container {
        padding: 2px;
      }
    }
  }
  // States
  .bp-input__container:focus-within {
    border-color: #6700ee;
    background-color: white;
  }
  &.error {
    .bp-input__container {
      border-color: #ff5053;
    }
    .bp-input__error {
      color: #bf2e30;
      font-size: 14px;
    }
  }

  &.disabled {
    .bp-input__container {
      background-color: #1919220a;
      border-color: transparent;

      .bp-input__inner {
        color: #c0c0c0;
        &.link {
          color: #6700ee;
        }
        &::placeholder {
          color: #c0c0c0;
        }
      }
    }
  }
  textarea {
    resize: none;
  }
}
</style>
